import { useEffect, useState, type FormEvent } from 'react';
import { ShieldCheck, Award, Contact, Gauge, LifeBuoy, LogOut, Megaphone, Menu, Percent, Settings2, Users, Wallet } from 'lucide-react';
import { MarketingAccount, PendingApproval } from './MarketingAccount';
import { CustomLink, MyClients } from './Extra';
import { Support } from './Support';
import { KycBanner, Verify } from './Verify';
import { Account, CodeButton, Commissions, CopyField, Hero, Kpis, Marketing, Payouts } from './Promo';
import { AuthStage, Logo, PasswordInput, ThemeToggle, useFavicon, useTheme, UpdateNotice, type ReleaseNote, type ThemeName } from '@ck/ui';
import { applyBranding, dateLabel, usd, type PublicTenant } from '@ck/sdk';
import { api, CLIENT_URL, session, setSignedOutHandler } from './api';
import { Empty, Failed, Loading, Note, useAction, useApi } from './ui';

interface Partner { id: string; email: string; name: string; code: string; commissionPct: number; status: string; createdAt: string; approval?: string; approvalNote?: string | null; kycStatus?: string; kycNote?: string | null; emailVerified?: boolean }
interface Dash {
  partner: Partner;
  stats: { clicks: number; clicks30: number; signups: number; buyers: number; salesUsd: number; conversionPct: number | null };
  balance: { earnedUsd: number; paidUsd: number; availableUsd: number; pendingUsd: number };
  monthly: { month: string; usd: number }[];
  minPayoutUsd: number;
}

const PAGES = [['dashboard', 'Dashboard', <Gauge key="i" />], ['referrals', 'Referrals', <Users key="i" />], ['clients', 'My clients', <Contact key="i" />], ['commissions', 'Commissions', <Percent key="i" />], ['payouts', 'Payouts', <Wallet key="i" />], ['marketing', 'Marketing kit', <Megaphone key="i" />], ['mkt', 'Marketing account', <Award key="i" />], ['support', 'Support', <LifeBuoy key="i" />], ['verify', 'Verification', <ShieldCheck key="i" />], ['account', 'Account', <Settings2 key="i" />]] as const;
type Page = (typeof PAGES)[number][0];

export function App() {
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [boot, setBoot] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  const [theme, setTheme] = useTheme('ck.partners.theme');
  useFavicon(tenant?.branding);
  useEffect(() => {
    setSignedOutHandler(() => setPartner(null));
    (async () => {
      try {
        const t = await api.tenant();
        setTenant(t); applyBranding(t.branding); document.title = `${t.branding.name} partners`;
        if (session.get()) setPartner((await api.get<{ partner: Partner }>('/v1/partner/me').catch(() => ({ partner: null }))).partner);
      } catch (e) { setFatal(e instanceof Error ? e.message : 'Something went wrong.'); }
      finally { setBoot(false); }
    })();
  }, []);
  if (tenant?.maintenance?.on) return <div className="maint-page"><Logo branding={tenant.branding} on="dark" height={40} /><h1>We’ll be right back</h1><p>{tenant.maintenance.message}</p></div>;
  if (fatal) return <div className="fatal"><b>We couldn’t load the portal.</b><span>{fatal}</span></div>;
  if (boot || !tenant) return <div className="boot"><span className="boot-mark">·</span></div>;
  if (!partner) return <Auth tenant={tenant} onIn={setPartner} />;
  if (partner.approval && partner.approval !== 'approved') return <PendingApproval name={partner.name} note={partner.approvalNote ?? null} rejected={partner.approval === 'rejected'} signOut={async () => { await api.post('/v1/partner/logout').catch(() => undefined); session.set(null); setPartner(null); }} />;
  return <Portal theme={theme} setTheme={setTheme} tenant={tenant} partner={partner} refresh={async () => setPartner((await api.get<{ partner: Partner }>('/v1/partner/me')).partner)} signOut={async () => { await api.post('/v1/partner/logout').catch(() => undefined); session.set(null); setPartner(null); }} />;
}

/** A staff member's link (…/?ref=CODE) records who referred this IB. Kept for the visit. */
const refCode = () => { try { const q = new URLSearchParams(location.search).get('ref') ?? /[?&#]ref=([A-Za-z0-9_-]+)/.exec(location.hash)?.[1]; if (q) sessionStorage.setItem('ck.ib.ref', q); return sessionStorage.getItem('ck.ib.ref') ?? undefined; } catch { return undefined; } };

function Auth({ tenant, onIn }: { tenant: PublicTenant; onIn: (p: Partner) => void }) {
  const [mode, setMode] = useState<'login' | 'signup' | 'verify'>('login');
  const [f, setF] = useState({ name: '', email: '', password: '', accept: false });
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const a = useAction();
  const b = tenant.branding;
  // The button in the verification email opens …/?verify=email&code=123456 and confirms in one tap.
  useEffect(() => {
    const q = new URLSearchParams(location.search); const em = q.get('verify'), c = q.get('code');
    if (!em) return;
    history.replaceState(null, '', location.pathname + location.hash);
    setF((v) => ({ ...v, email: em })); setMode('verify');
    if (c && /^\d{6}$/.test(c)) { setCode(c); void a.run(() => api.post('/v1/partner/email/confirm', { email: em, code: c })).then((r) => { if (r) { setVerified(true); setMode('login'); } }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'verify') {
      if (await a.run(() => api.post('/v1/partner/email/confirm', { email: f.email, code }))) { setVerified(true); setMode('login'); setCode(''); }
      return;
    }
    const r = await a.run(() => mode === 'login'
      ? api.post<{ token?: string; partner?: Partner; verify?: boolean }>('/v1/partner/login', { email: f.email, password: f.password })
      : api.post<{ token?: string; partner?: Partner; verify?: boolean }>('/v1/partner/signup', { name: f.name, email: f.email, password: f.password, acceptTerms: f.accept, ref: refCode() }));
    if (!r) return;
    if (r.verify || !r.token) { setVerified(false); setMode('verify'); return; }
    session.set(r.token); onIn(r.partner!);
  }
  return (
    <AuthStage branding={b} product="Partners" videoUrl="/media/partners-bg.mp4"
      headline={mode === 'verify' ? <>Check your inbox.<span className="pa-gold">One step left.</span></> : mode === 'signup' ? <>Bring traders.<span className="pa-gold">Earn on every challenge.</span></> : <>Welcome back,<span className="pa-gold">partner.</span></>}
      sub="Your link, your referrals and your commission in one place, updated live."
      points={['Earn commission on every paid challenge', 'Track visits, sign-ups and sales live', 'Get paid by mobile money, bank or USDT']}>
      {mode === 'verify' ? (
        <form className="form" onSubmit={submit}>
          <div className="pa-verify"><b>Verify your email</b><span>We sent a 6-digit code to <b>{f.email}</b>. Enter it below, or tap the button in the email.</span></div>
          <label>Verification code<div className="pa-otp"><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" />
            <button type="button" onClick={async () => { try { const t = (await navigator.clipboard.readText()).replace(/\D/g, '').slice(0, 6); if (t) setCode(t); } catch { /* */ } }}>Paste</button></div></label>
          {a.error && <Note kind="err">{a.error}</Note>}{a.done && <Note>{a.done}</Note>}
          <button className="btn primary wide" disabled={a.busy || code.length !== 6}>{a.busy ? 'Checking…' : 'Verify email'}</button>
          <p className="fine">No email? Check spam, or <button type="button" className="link" onClick={() => void a.run(() => api.post('/v1/partner/email/resend', { email: f.email }), 'A new code is on its way.')}>send a new code</button>. Wrong email? <button type="button" className="link" onClick={() => setMode('signup')}>Go back</button></p>
        </form>
      ) : <>
      {verified && <div className="pa-verify ok"><b>Email verified</b><span>Your IB account is active. Sign in to open your partner portal.</span></div>}
      <div className="tabs2" role="tablist">
        <button role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => setMode('login')}>Sign in</button>
        <button role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'on' : ''} onClick={() => setMode('signup')}>Become a partner</button>
      </div>
      <form className="form" onSubmit={submit}>
        {mode === 'signup' && <label>Your name or brand<input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>}
        <label>Email<input type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
        <label>Password<PasswordInput required autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={mode === 'signup' ? 10 : 1} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />{mode === 'signup' && <small>At least 10 characters, with letters and a number.</small>}</label>
        {mode === 'signup' && <label className="check"><input type="checkbox" required checked={f.accept} onChange={(e) => setF({ ...f, accept: e.target.checked })} /> I accept the partner terms and will promote responsibly, without promising profits.</label>}
        {a.error && <Note kind="err">{a.error}</Note>}
        <button className="btn primary wide" disabled={a.busy}>{a.busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create partner account'}</button>
      </form>
      <p className="fine">Already a trader with us? Join from the Affiliate page in your client portal, then sign in here with the same email and password.</p>
      </>}
    </AuthStage>
  );
}

function Portal({ theme, setTheme, tenant, partner, signOut, refresh }: { theme: ThemeName; setTheme: (t: ThemeName) => void; tenant: PublicTenant; partner: Partner; signOut: () => void; refresh: () => Promise<void> }) {
  const [page, setPage] = useState<Page>(() => (PAGES.find((p) => `#/${p[0]}` === location.hash)?.[0] ?? 'dashboard'));
  const [menu, setMenu] = useState(false);
  // On a computer the three lines open or close the menu, and the choice is remembered.
  const [closed, setClosed] = useState(() => { try { return localStorage.getItem('ck.ib.side') === 'closed'; } catch { return false; } });
  const toggle = () => {
    if (window.matchMedia('(max-width: 640px)').matches) { setMenu(!menu); return; }
    setClosed((c) => { try { localStorage.setItem('ck.ib.side', c ? 'open' : 'closed'); } catch { /* */ } return !c; });
  };
  useEffect(() => { history.replaceState(null, '', `#/${page}`); setMenu(false); window.scrollTo(0, 0); }, [page]);
  const b = tenant.branding;
  const link = `${CLIENT_URL}/?ref=${partner.code}`;
  const initials = partner.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={`shell${menu ? ' menu-open' : ''}${closed ? ' side-closed' : ''}`}>
      <aside className="side">
        <a className="side-brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}><Logo branding={b} on="dark" height={26} /><small>Partners</small></a>
        <nav className="side-scroll">
          <div className="side-g">
            {PAGES.map(([id, label, icon]) => <a key={id} href={`#/${id}`} className={`side-a${page === id ? ' on' : ''}`} aria-current={page === id ? 'page' : undefined} onClick={(e) => { e.preventDefault(); setPage(id); }}><span className="ic">{icon}</span>{label}</a>)}
          </div>
        </nav>
        <div className="side-foot">
          <div className="side-me"><span className="av">{initials}</span><div><b>{partner.name}</b><small>{partner.commissionPct}% commission, code {partner.code}</small></div></div>
          <button className="side-out" onClick={signOut}><LogOut />Sign out</button>
        </div>
      </aside>
      {menu && <div className="scrim" onClick={() => setMenu(false)} />}
      <header className="top">
        <div className="top-in">
          <button className="burger" aria-label={closed ? 'Open menu' : 'Close menu'} aria-expanded={!closed} onClick={toggle}><Menu /></button>
          <a className="brand" href="#/dashboard" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}><Logo branding={b} compact height={30} on={theme === 'dark' ? 'dark' : 'light'} /></a>
          <span className="spacer" />
          <ThemeToggle theme={theme} onChange={setTheme} />
          <CodeButton code={partner.code} />
          <button className="ibx-out" onClick={signOut}><LogOut /><span>Sign out</span></button>
        </div>
      </header>
        <UpdateNotice latest={() => api.get<{ release: ReleaseNote | null }>('/v1/releases/latest').then((r) => r.release)} />
      <main className="main page-in" key={page}>
        {page !== 'verify' && <KycBanner status={partner.kycStatus ?? 'not_started'} note={partner.kycNote ?? null} onVerify={() => setPage('verify')} />}
        {page === 'verify' && <Verify status={partner.kycStatus ?? 'not_started'} note={partner.kycNote ?? null} done={() => void refresh()} />}
        {page === 'dashboard' && <Dashboard link={link} go={setPage} />}
        {page === 'referrals' && <Referrals />}
        {page === 'commissions' && <Commissions rate={partner.commissionPct} />}
        {page === 'payouts' && <Payouts tenant={tenant} kycOk={partner.kycStatus === 'approved'} goVerify={() => setPage('verify')} />}
        {page === 'marketing' && <><Marketing link={link} tenant={tenant} code={partner.code} /><CustomLink code={partner.code} clientUrl={CLIENT_URL} onChanged={() => void refresh()} /></>}
        {page === 'mkt' && <MarketingAccount tenant={tenant} />}
        {page === 'clients' && <MyClients />}
        {page === 'support' && <Support base="/v1/partner/support" />}
        {page === 'account' && <Account partner={partner} tenant={tenant} link={link} goVerify={() => setPage('verify')} refresh={refresh} />}
        <footer className="foot"><span>{tenant.legalName}</span><span>Promote responsibly: never promise profits. Trading carries risk.</span></footer>
      </main>
    </div>
  );
}

function Dashboard({ link, go }: { link: string; go: (p: Page) => void }) {
  const d = useApi(() => api.get<Dash>('/v1/partner/dashboard'));
  if (d.error) return <Failed error={d.error} retry={d.reload} />;
  if (!d.data) return <Loading />;
  const { stats: s, balance: b, monthly, partner } = d.data;
  const max = Math.max(1, ...monthly.map((m) => m.usd));
  return (
    <>
      <Hero kicker="Partner dashboard" title={`Welcome, ${partner.name.split(' ')[0]}`} lead={`You earn ${partner.commissionPct}% of every challenge bought by people you refer.`}
        side={<div className="ibx-split"><div><span>Available now</span><b>{usd(b.availableUsd)}</b></div><div><span>Earned in total</span><b>{usd(b.earnedUsd)}</b></div><button className="ibx-btn" onClick={() => go('payouts')}>Withdraw</button></div>}>
        <CopyField label="Your referral link" value={link} big />
      </Hero>
      <Kpis items={[
        ['Link visits', s.clicks, `${s.clicks30} in the last 30 days`],
        ['Sign-ups', s.signups, s.conversionPct !== null ? `${s.conversionPct}% of visits` : undefined],
        ['Paying clients', s.buyers],
        ['Sales you drove', usd(s.salesUsd)],
        ['Commission earned', usd(b.earnedUsd)],
        ['In progress', usd(b.pendingUsd)],
      ]} />
      <section className="ibx-card">
        <h3>Commission by month</h3>
        {monthly.length === 0 ? <Empty title="No commission yet.">Share your link to start earning.</Empty> : (
          <div className="bars">{monthly.map((m) => <div key={m.month} className="bar"><i style={{ height: `${(m.usd / max) * 100}%` }} title={usd(m.usd)} /><span className="mono">{m.month.slice(5)}/{m.month.slice(2, 4)}</span><b>{usd(m.usd)}</b></div>)}</div>
        )}
      </section>
    </>
  );
}

function Referrals() {
  const d = useApi(() => api.get<{ referrals: { name: string; country: string; joinedAt: string; paidOrders: number; spentUsd: number }[] }>('/v1/partner/referrals').then((r) => r.referrals));
  return (
    <>
      <Hero kicker="Referrals" title={d.data ? `${d.data.length} referral${d.data.length === 1 ? '' : 's'}` : 'Referrals'} lead="Everyone who signed up through your link. Contact details stay private." />
      {d.data && d.data.length > 0 && <Kpis items={[['Signed up', d.data.length], ['Paying', d.data.filter((r) => r.paidOrders > 0).length], ['Challenges bought', d.data.reduce((n, r) => n + r.paidOrders, 0)], ['They spent', usd(d.data.reduce((n, r) => n + r.spentUsd, 0))]]} />}
      {d.error ? <Failed error={d.error} /> : !d.data ? <Loading /> : d.data.length === 0 ? <Empty title="No referrals yet." /> : (
        <div className="tw"><table className="tbl"><thead><tr><th>Client</th><th>Country</th><th>Joined</th><th>Paid challenges</th><th className="r">Spent</th></tr></thead>
          <tbody>{d.data.map((r, i) => <tr key={i}><td>{r.name}</td><td>{r.country}</td><td>{dateLabel(r.joinedAt)}</td><td>{r.paidOrders}</td><td className="r">{usd(r.spentUsd)}</td></tr>)}</tbody></table></div>
      )}
    </>
  );
}

